/**
 * F6 — the description of an app (`tool_apps.app_spec`): pages of blocks with
 * stable ids, drawn by the code from Domino's own catalogue. A block reads only
 * a declared source (the app's list, its reviews, its runs); nothing here is
 * code or a query. Without a stored description, an app is described by its
 * current screens (`defaultAppSpec`), so nothing changes on screen.
 */

export const APP_SPEC_VERSION = 1 as const;

/** A field of an object of the list: a cell of its row, or one of people's fields. */
export type AppField = string;
/** People's fields, besides the cells the runs produce. */
export const APP_PEOPLE_FIELDS = ['status', 'assignee', 'owner'] as const;

export type AppAggregate =
  | { op: 'count' }
  | { op: 'avg' | 'sum' | 'min' | 'max'; field: AppField };

/** Keeps the objects whose field has one of these values (status keys, cell values). */
export interface AppFilter {
  field: AppField;
  in: string[];
}

export interface AppMetricItem {
  label: string;
  aggregate: AppAggregate;
  filter?: AppFilter[];
}

/** Bars or a line: one category per value of a field, or one per listed field (e.g. one per criterion). */
export type AppChartX =
  | { by: 'field'; field: AppField }
  | { by: 'fields'; fields: Array<{ field: AppField; label: string }> };

export type AppBlockWidth = 'full' | 'half';

interface AppBlockBase {
  /** Stable id, unique in the description: patches target it. */
  id: string;
  width?: AppBlockWidth;
}

export type AppBlock =
  | AppBlockBase & { type: 'records'; label?: string; columns?: AppField[]; filter?: AppFilter[] }
  | AppBlockBase & { type: 'metrics'; label?: string; items: AppMetricItem[] }
  | AppBlockBase & {
    type: 'chart'; label: string; kind: 'bar' | 'line'; x: AppChartX;
    /** For `by: 'field'`: the measure per category. For `by: 'fields'`: the operation applied to each field. */
    aggregate: AppAggregate | { op: 'avg' | 'sum' | 'min' | 'max' };
    /** One series per value of this field (e.g. per salesperson); one series when absent. */
    series?: AppField;
    unit?: string;
    filter?: AppFilter[];
  }
  | AppBlockBase & { type: 'team_review' }
  | AppBlockBase & { type: 'runs' }
  /** « Trouver et contacter » (03/11/2026): whom to contact at each object, their address, the e-mail sequence. */
  | AppBlockBase & { type: 'sequence' };

export type AppBlockType = AppBlock['type'];
export const APP_BLOCK_TYPES: readonly AppBlockType[] = ['records', 'metrics', 'chart', 'team_review', 'runs', 'sequence'];

export interface AppPage {
  id: string;
  label: string;
  blocks: AppBlock[];
}

export interface AppSpec {
  version: typeof APP_SPEC_VERSION;
  pages: AppPage[];
  /**
   * What the app's main button does. Absent: launch its tool. `import`
   * (03/11/2026): an app whose list is filled from a table (« Prospection »).
   */
  start?: 'import';
}

const ID = /^[a-z][a-z0-9_-]{0,63}$/;
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const text = (value: unknown, max = 160): string | null =>
  typeof value === 'string' && value.trim() && value.trim().length <= max ? value.trim() : null;
const field = (value: unknown): AppField | null => typeof value === 'string' && /^[a-z][a-z0-9_]{0,79}$/.test(value) ? value : null;

const parseAggregate = (value: unknown, allowFieldless: boolean): AppAggregate | { op: 'avg' | 'sum' | 'min' | 'max' } | null => {
  if (!isRecord(value)) return null;
  if (value.op === 'count') return { op: 'count' };
  if (value.op !== 'avg' && value.op !== 'sum' && value.op !== 'min' && value.op !== 'max') return null;
  const target = field(value.field);
  if (target) return { op: value.op, field: target };
  return allowFieldless ? { op: value.op } : null;
};

const parseFilters = (value: unknown): AppFilter[] | undefined | null => {
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) return null;
  const filters = value.map((item) => {
    const target = isRecord(item) ? field(item.field) : null;
    const values = isRecord(item) && Array.isArray(item.in) ? item.in.filter((entry): entry is string => typeof entry === 'string') : [];
    return target && values.length ? { field: target, in: values } : null;
  });
  return filters.every((item) => item !== null) ? filters as AppFilter[] : null;
};

const parseBlock = (value: unknown): AppBlock | null => {
  if (!isRecord(value) || typeof value.id !== 'string' || !ID.test(value.id)) return null;
  const base: AppBlockBase = { id: value.id, ...(value.width === 'half' || value.width === 'full' ? { width: value.width } : {}) };
  const label = text(value.label);
  const filter = parseFilters(value.filter);
  if (filter === null) return null;
  switch (value.type) {
    case 'records': {
      const columns = Array.isArray(value.columns) ? value.columns.map(field) : undefined;
      if (columns && columns.some((column) => column === null)) return null;
      return { ...base, type: 'records', ...(label ? { label } : {}), ...(columns ? { columns: columns as AppField[] } : {}), ...(filter ? { filter } : {}) };
    }
    case 'metrics': {
      if (!Array.isArray(value.items) || value.items.length === 0) return null;
      const items = value.items.map((item) => {
        const itemLabel = isRecord(item) ? text(item.label, 80) : null;
        const aggregate = isRecord(item) ? parseAggregate(item.aggregate, false) as AppAggregate | null : null;
        const itemFilter = isRecord(item) ? parseFilters(item.filter) : null;
        return itemLabel && aggregate && itemFilter !== null ? { label: itemLabel, aggregate, ...(itemFilter ? { filter: itemFilter } : {}) } : null;
      });
      return items.every((item) => item !== null) ? { ...base, type: 'metrics', ...(label ? { label } : {}), items: items as AppMetricItem[] } : null;
    }
    case 'chart': {
      const x = isRecord(value.x) ? value.x : null;
      let parsedX: AppChartX | null = null;
      if (x?.by === 'field' && field(x.field)) parsedX = { by: 'field', field: field(x.field)! };
      if (x?.by === 'fields' && Array.isArray(x.fields) && x.fields.length > 0) {
        const fields = x.fields.map((item) => isRecord(item) && field(item.field) && text(item.label, 80) ? { field: field(item.field)!, label: text(item.label, 80)! } : null);
        if (fields.every((item) => item !== null)) parsedX = { by: 'fields', fields: fields as Array<{ field: AppField; label: string }> };
      }
      const aggregate = parseAggregate(value.aggregate, parsedX?.by === 'fields');
      const series = value.series === undefined ? undefined : field(value.series);
      if (!label || !parsedX || !aggregate || (value.kind !== 'bar' && value.kind !== 'line') || series === null) return null;
      return {
        ...base, type: 'chart', label, kind: value.kind, x: parsedX, aggregate,
        ...(series ? { series } : {}), ...(text(value.unit, 12) ? { unit: text(value.unit, 12)! } : {}), ...(filter ? { filter } : {}),
      };
    }
    case 'team_review': return { ...base, type: 'team_review' };
    case 'runs': return { ...base, type: 'runs' };
    case 'sequence': return { ...base, type: 'sequence' };
    default: return null;
  }
};

/**
 * Reads a stored description. Returns the issues instead of guessing: an
 * invalid page or block is dropped and named, never replaced by something else.
 */
export const parseAppSpec = (value: unknown): { spec: AppSpec | null; issues: string[] } => {
  if (!isRecord(value) || value.version !== APP_SPEC_VERSION || !Array.isArray(value.pages)) {
    return { spec: null, issues: value == null ? [] : ['description d’app illisible'] };
  }
  const issues: string[] = [];
  const ids = new Set<string>();
  const pages: AppPage[] = [];
  for (const page of value.pages) {
    const pageId = isRecord(page) && typeof page.id === 'string' && ID.test(page.id) ? page.id : null;
    const pageLabel = isRecord(page) ? text(page.label, 60) : null;
    if (!pageId || !pageLabel || !Array.isArray((page as Record<string, unknown>).blocks) || ids.has(pageId)) {
      issues.push(`page ${pageId ?? '?'} ignorée`);
      continue;
    }
    ids.add(pageId);
    const blocks: AppBlock[] = [];
    for (const raw of (page as { blocks: unknown[] }).blocks) {
      const block = parseBlock(raw);
      if (!block || ids.has(block.id)) {
        issues.push(`bloc ${isRecord(raw) && typeof raw.id === 'string' ? raw.id : '?'} ignoré`);
        continue;
      }
      ids.add(block.id);
      blocks.push(block);
    }
    pages.push({ id: pageId, label: pageLabel, blocks });
  }
  return pages.length
    ? { spec: { version: APP_SPEC_VERSION, pages, ...(value.start === 'import' ? { start: 'import' as const } : {}) }, issues }
    : { spec: null, issues: [...issues, 'aucune page lisible'] };
};

/** The app's current screens, described: its list, the team review when it has a grid, its runs. */
export const defaultAppSpec = (options: { hasReviewGrid: boolean }): AppSpec => ({
  version: APP_SPEC_VERSION,
  pages: [
    { id: 'liste', label: 'Liste', blocks: [{ id: 'liste-objets', type: 'records' }] },
    ...(options.hasReviewGrid ? [{ id: 'equipe', label: 'Équipe', blocks: [{ id: 'equipe-revue', type: 'team_review' as const }] }] : []),
    { id: 'executions', label: 'Exécutions', blocks: [{ id: 'executions-runs', type: 'runs' }] },
  ],
});
